import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

function getColorFromString(str) {
  let hash = 0;
  const input = String(str || '');

  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash % 360);

  return {
    borderColor: 'hsl(' + hue + ', 70%, 42%)',
    backgroundColor: 'hsla(' + hue + ', 70%, 42%, 0.15)',
    pointBackgroundColor: 'hsl(' + hue + ', 70%, 42%)'
  };
}

function Ventoo() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!sessionStorage.getItem('ventoo_password')
  );
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  const [products, setProducts] = useState([]);
  const [worker, setWorker] = useState({});
  const [state, setState] = useState({});
  const [newUrl, setNewUrl] = useState('');
  const [intervalHours, setIntervalHours] = useState(4);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [historiesByUrl, setHistoriesByUrl] = useState({});

  const isMobile = useMemo(() => {
    return typeof window !== 'undefined' ? window.innerWidth <= 768 : false;
  }, []);

  const fetchVentooData = async () => {
    try {
      setLoading(true);
      setMessage('');

      const response = await api.get('/api/ventoo/products');
      const fetchedProducts = response.data.products || [];

      fetchedProducts.sort((a, b) => Number(b.changesCount || 0) - Number(a.changesCount || 0));

      setProducts(fetchedProducts);
      setWorker(response.data.worker || {});
      setState(response.data.state || {});
      setIntervalHours(Number(response.data.state?.intervalHours || 4));

      await fetchAllHistories(fetchedProducts);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem('ventoo_password');
        setIsAuthenticated(false);
        setAuthError('Parola a expirat sau este gresita.');
        return;
      }

      setMessage(
        err?.response?.data?.error || err?.message || 'Nu am putut incarca datele VENTOO'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAllHistories = async (productsList) => {
    const entries = await Promise.all(
      productsList.map(async (product) => {
        try {
          const response = await api.get('/api/ventoo/history', {
            params: { url: product.url }
          });

          const history = (response.data.history || [])
            .slice()
            .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));

          return [product.url, history];
        } catch (err) {
          return [product.url, []];
        }
      })
    );

    const mapped = {};
    entries.forEach(([url, history]) => {
      mapped[url] = history;
    });

    setHistoriesByUrl(mapped);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchVentooData();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!password.trim()) {
      setAuthError('Introdu parola.');
      return;
    }

    try {
      setAuthError('');

      await api.post('/api/ventoo/login', {
        password: password.trim()
      });

      sessionStorage.setItem('ventoo_password', password.trim());
      setIsAuthenticated(true);
      setPassword('');
    } catch (err) {
      setAuthError(err?.response?.data?.error || 'Parola incorecta');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('ventoo_password');
    setIsAuthenticated(false);
    setProducts([]);
    setHistoriesByUrl({});
    setMessage('');
  };

  const handleAddProduct = async () => {
    if (!newUrl.trim()) {
      setMessage('Introdu un link.');
      return;
    }

    try {
      await api.post('/api/ventoo/products', {
        url: newUrl.trim()
      });

      setMessage('Produs adaugat cu succes.');
      setNewUrl('');
      fetchVentooData();
    } catch (err) {
      setMessage(err?.response?.data?.error || 'Nu am putut adauga produsul.');
    }
  };

  const handleDeleteProduct = async (url) => {
    const confirmed = window.confirm('Sigur vrei sa stergi acest produs?');
    if (!confirmed) return;

    try {
      await api.delete('/api/ventoo/products', {
        data: { url }
      });

      setMessage('Produs sters cu succes.');
      fetchVentooData();
    } catch (err) {
      setMessage(err?.response?.data?.error || 'Nu am putut sterge produsul.');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.patch('/api/ventoo/settings', {
        intervalHours: Number(intervalHours)
      });

      setMessage('Interval actualizat.');
      fetchVentooData();
    } catch (err) {
      setMessage(err?.response?.data?.error || 'Nu am putut salva setarile.');
    }
  };

  const topOpportunities = useMemo(() => {
    return products
      .map((product) => {
        const history = historiesByUrl[product.url] || [];
        const last = history[history.length - 1];
        const prev = history[history.length - 2];

        const lastQty = Number(last?.quantity ?? product.lastQuantity ?? 0);
        const prevQty = Number(prev?.quantity ?? lastQty);
        const drop = Math.max(0, prevQty - lastQty);

        return {
          url: product.url,
          title: product.title || extractProductTitle(product.url),
          drop,
          lastQty,
          changesCount: Number(product.changesCount || 0)
        };
      })
      .filter((item) => item.drop > 0)
      .sort((a, b) => {
        if (b.drop !== a.drop) return b.drop - a.drop;
        return b.changesCount - a.changesCount;
      })
      .slice(0, 5);
  }, [products, historiesByUrl]);

  if (!isAuthenticated) {
    return (
      <div style={pageStyle}>
        <div style={loginCardStyle}>
          <h1 style={titleStyle}>VENTOO</h1>
          <p style={subtitleStyle}>Introdu parola pentru acces.</p>

          <form onSubmit={handleLogin}>
            <label style={labelStyle}>Parola</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              autoComplete="current-password"
            />

            <button type="submit" style={buttonStyle}>
              Intra
            </button>
          </form>

          {authError ? <div style={errorStyle}>{authError}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={topBarStyle}>
        <div>
          <h1 style={titleStyle}>VENTOO</h1>
          <p style={subtitleStyle}>Crawler produse eMAG</p>
        </div>

        <button onClick={handleLogout} style={logoutButtonStyle}>
          Logout
        </button>
      </div>

      {message ? <div style={infoStyle}>{message}</div> : null}

      <div style={gridStyle}>
        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Adauga produs</h2>
          <input
            type="text"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="Link produs eMAG"
            style={inputStyle}
          />
          <button onClick={handleAddProduct} style={buttonStyle}>
            Adauga
          </button>
        </div>

        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Setari</h2>
          <label style={labelStyle}>Interval verificare (ore)</label>
          <input
            type="number"
            min="1"
            max="24"
            value={intervalHours}
            onChange={(e) => setIntervalHours(e.target.value)}
            style={inputStyle}
          />
          <button onClick={handleSaveSettings} style={buttonStyle}>
            Salveaza
          </button>

          <div style={metaTextStyle}>
            Worker: {worker?.status || 'unknown'}
          </div>
          <div style={metaTextStyle}>
            Ultima rulare: {state?.lastRunAt ? new Date(state.lastRunAt).toLocaleString('ro-RO') : '-'}
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Charts</h2>
        <div style={metaTextStyle}>Produse monitorizate: {products.length}</div>

        <div style={{ marginTop: '14px' }}>
          <div style={miniSectionTitleStyle}>Top oportunitati</div>

          {topOpportunities.length === 0 ? (
            <div style={metaTextStyle}>Nu exista scaderi recente detectate.</div>
          ) : (
            <div style={opportunityListStyle}>
              {topOpportunities.map((item) => (
                <div key={item.url} style={opportunityItemStyle}>
                  <div style={opportunityTitleStyle}>{item.title}</div>
                  <div style={opportunityMetaStyle}>
                    Scadere: -{item.drop} | Stoc curent: {item.lastQty}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={chartsWrapStyle}>
        {products.map((product) => {
          const history = historiesByUrl[product.url] || [];
          const chartData = buildChartData(history, product.url);

          return (
            <div key={product.url} style={chartCardStyle}>
              <div style={chartHeaderStyle}>
                <div style={chartTitleWrapStyle}>
                  <div style={chartTitleStyle}>
                    {product.title || extractProductTitle(product.url)}
                  </div>
                  <div style={chartMetaStyle}>
                    Modificari: {product.changesCount || 0}
                  </div>
                  <div style={chartMetaStyle}>
                    Ultima cantitate: {product.lastQuantity ?? '-'}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap',
                    justifyContent: isMobile ? 'flex-start' : 'flex-end'
                  }}
                >
                  <a href={product.url} target="_blank" rel="noreferrer" style={openLinkStyle}>
                    Link
                  </a>

                  <button
                    onClick={() => handleDeleteProduct(product.url)}
                    style={deleteButtonStyle}
                    title="Sterge produs"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div style={chartBoxStyle}>
                {history.length < 1 ? (
                  <div style={metaTextStyle}>Nu exista istoric pentru acest produs.</div>
                ) : (
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      animation: false,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          backgroundColor: '#111827',
                          titleColor: '#fff',
                          bodyColor: '#fff',
                          padding: 10,
                          displayColors: false,
                          callbacks: {
                            title: (items) => {
                              const label = items?.[0]?.label;
                              return label || '';
                            },
                            label: (context) => {
                              return 'Stoc: ' + context.parsed.y;
                            }
                          }
                        }
                      },
                      elements: {
                        line: {
                          tension: 0.4
                        },
                        point: {
                          radius: history.length <= 1 ? 5 : 4
                        }
                      },
                      scales: {
                        x: {
                          grid: {
                            display: false
                          },
                          ticks: {
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 6
                          }
                        },
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: 'rgba(0,0,0,0.05)'
                          }
                        }
                      }
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function buildChartData(history, key) {
  const colors = getColorFromString(key);

  const values = history.map((entry) => Number(entry.quantity ?? 0));
  const backgroundColors = values.map((value, index) => {
    if (index === 0) {
      return colors.backgroundColor;
    }

    const previous = values[index - 1];

    if (value < previous) {
      return 'rgba(220, 38, 38, 0.65)';
    }

    if (value > previous) {
      return 'rgba(22, 163, 74, 0.65)';
    }

    return colors.backgroundColor;
  });

  const borderColors = values.map((value, index) => {
    if (index === 0) {
      return colors.borderColor;
    }

    const previous = values[index - 1];

    if (value < previous) {
      return 'rgb(185, 28, 28)';
    }

    if (value > previous) {
      return 'rgb(21, 128, 61)';
    }

    return colors.borderColor;
  });

  return {
    labels: history.map((entry) =>
      new Date(entry.dateTime).toLocaleString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    ),
    datasets: [
      {
        label: 'Stoc',
        data: values,
        borderColor: borderColors,
        backgroundColor: backgroundColors,
        borderWidth: 2,
        borderRadius: 6,
        barThickness: 'flex'
      }
    ]
  };
}

function extractProductTitle(url) {
  try {
    const cleaned = String(url || '').split('/pd/')[0];
    const parts = cleaned.split('/');
    return decodeURIComponent(parts[parts.length - 1] || url);
  } catch (e) {
    return url;
  }
}

const pageStyle = {
  maxWidth: '1400px',
  margin: '0 auto'
};

const loginCardStyle = {
  maxWidth: '420px',
  margin: '60px auto',
  background: '#fff',
  padding: '24px',
  borderRadius: '12px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
};

const topBarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px'
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '20px',
  marginBottom: '20px'
};

const chartsWrapStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '18px',
  marginBottom: '20px'
};

const cardStyle = {
  background: '#fff',
  padding: '20px',
  borderRadius: '12px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  marginBottom: '20px'
};

const chartCardStyle = {
  background: '#fff',
  padding: '16px',
  borderRadius: '12px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
};

const chartHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  marginBottom: '12px'
};

const chartTitleWrapStyle = {
  minWidth: 0
};

const chartTitleStyle = {
  fontWeight: '800',
  color: '#111827',
  marginBottom: '6px',
  wordBreak: 'break-word',
  fontSize: typeof window !== 'undefined' && window.innerWidth <= 768 ? '17px' : '18px'
};

const chartMetaStyle = {
  color: '#4b5563',
  fontSize: '13px',
  marginBottom: '4px'
};

const chartBoxStyle = {
  height: typeof window !== 'undefined' && window.innerWidth <= 768 ? '180px' : '220px'
};

const titleStyle = {
  margin: 0,
  fontSize: '30px',
  color: '#111827'
};

const subtitleStyle = {
  marginTop: '8px',
  color: '#4b5563'
};

const sectionTitleStyle = {
  marginTop: 0,
  marginBottom: '16px',
  color: '#111827'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '600'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  marginBottom: '12px',
  boxSizing: 'border-box'
};

const buttonStyle = {
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 18px',
  fontWeight: '700',
  cursor: 'pointer'
};

const logoutButtonStyle = {
  background: '#111827',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '10px 16px',
  fontWeight: '700',
  cursor: 'pointer'
};

const deleteButtonStyle = {
  background: '#dc2626',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  width: '38px',
  height: '38px',
  fontWeight: '800',
  fontSize: '20px',
  cursor: 'pointer'
};

const openLinkStyle = {
  color: '#2563eb',
  fontWeight: '700',
  textDecoration: 'none',
  border: '1px solid #2563eb',
  borderRadius: '8px',
  padding: '9px 14px',
  display: 'inline-flex',
  alignItems: 'center'
};

const infoStyle = {
  background: '#e0f2fe',
  color: '#075985',
  padding: '12px 16px',
  borderRadius: '10px',
  marginBottom: '16px',
  fontWeight: '600'
};

const errorStyle = {
  background: '#fee2e2',
  color: '#991b1b',
  padding: '12px 16px',
  borderRadius: '10px',
  marginTop: '12px',
  fontWeight: '600'
};

const metaTextStyle = {
  color: '#4b5563',
  marginTop: '8px'
};

const miniSectionTitleStyle = {
  fontWeight: '800',
  color: '#111827',
  marginBottom: '10px'
};

const opportunityListStyle = {
  display: 'grid',
  gap: '10px'
};

const opportunityItemStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  padding: '10px 12px',
  background: '#fafafa'
};

const opportunityTitleStyle = {
  fontWeight: '700',
  color: '#111827',
  marginBottom: '4px'
};

const opportunityMetaStyle = {
  color: '#b91c1c',
  fontWeight: '700',
  fontSize: '13px'
};

const tableWrapStyle = {
  overflowX: 'auto'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const thStyle = {
  textAlign: 'left',
  padding: '12px',
  background: '#f3f4f6',
  borderBottom: '1px solid #e5e7eb'
};

const tdStyle = {
  padding: '12px',
  borderBottom: '1px solid #e5e7eb'
};

export default Ventoo;